import { DateTime } from 'luxon';
import { Pipe, PipeTransform } from '@angular/core';

/**
 * A Pipe that transforms the duration of a date from now (current time) into a human readable expression.
 *
 * ## Usage in Angular Template
 *
 *   {{ someDate | sgFromNow }}
 *   {{ someDate | sgFromNow: { maxGapDays: 30 } }}
 *   {{ someDate | sgFromNow: { maxGapDays: 3, fallbackFormat: 'yyyy-MM-dd' } }}
 *
 * ## Arguments
 * - `maxGapDays` (optional): Maximum number of days to use relative format (default: 7). If the difference exceeds this, the fallback format is used.
 * - `fallbackFormat` (optional): Format string or Luxon preset for dates beyond maxGapDays (default: DateTime.DATETIME_MED).
 *
 * ## Examples
 *
 *   // If today is 2025-05-21
 *   {{ '2025-05-20T10:00:00' | sgFromNow }}
 *   // Output: 1 day ago
 *
 *   {{ '2025-05-01T10:00:00' | sgFromNow: { maxGapDays: 7 } }}
 *   // Output: May 1, 2025, 10:00 AM
 *
 *   {{ '2025-05-14T10:00:00' | sgFromNow }}
 *   // Output: 1 week ago
 *
 *   {{ '2025-05-21T09:00:00' | sgFromNow }}
 *   // Output: 1 hour ago
 *
 *   {{ '2025-05-21T10:00:00' | sgFromNow }}
 *   // Output: just now
 *
 *   {{ 'invalid-date' | sgFromNow }}
 *   // Output: Invalid date
 */
@Pipe({
  name: 'sgFromNow',
  standalone: false,
})
export class FromNowPipe implements PipeTransform {
  transform(value: any, args?: { maxGapDays?: number; fallbackFormat?: string } | any): any {
    // Accept optional max gap and fallback format
    const maxGapDays = args?.maxGapDays ?? 7;
    const fallbackFormat = args?.fallbackFormat ?? DateTime.DATETIME_MED;

    // Parse date
    const date = DateTime.fromISO(value);
    if (!date.isValid) return 'Invalid date';

    // Calculate difference
    const diff = date.diffNow(['days', 'hours', 'minutes', 'seconds']).negate();

    // Use fallback format if max gap reached
    if (diff.days > maxGapDays) {
      return date.toLocaleString(fallbackFormat);
    }

    // Weeks
    if (diff.days >= 14) {
      const weeks = Math.round(diff.days / 7);
      return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    }
    // Days
    if (diff.days >= 1) {
      const days = Math.round(diff.days);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    }
    // Hours
    const hours = diff.as('hours');
    if (hours >= 1) {
      const rounded = Math.round(hours);
      return `${rounded} hour${rounded === 1 ? '' : 's'} ago`;
    }
    // Minutes
    const minutes = diff.as('minutes');
    if (minutes >= 2) {
      const rounded = Math.round(minutes);
      return `${rounded} minute${rounded === 1 ? '' : 's'} ago`;
    }
    // Seconds
    const seconds = diff.as('seconds');
    if (seconds >= 10) {
      const rounded = Math.round(seconds);
      return `${rounded} second${rounded === 1 ? '' : 's'} ago`;
    }
    return 'just now';
  }
}
